import * as React from 'react'
import { Camera, Loader2, TriangleAlert } from 'lucide-react'
import { ALLOWED_PHOTO_CONTENT_TYPES } from '@lib/constants/documents'
import { errorMessage } from '@/helpers/logger'
import { useUploadDocument } from '@/hooks/useDocumentMutations'
import { assertUsablePhoto, usePhotoUrl } from '@/hooks/usePhotos'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'

/**
 * A problem type's stock photograph, or a placeholder when it has none.
 *
 * Storage is private, so the thumbnail asks for its own short-lived link
 * rather than pointing at a bucket path — the same reason a tree photo does.
 */
export function StockPhotoThumb({ documentId }: { documentId: string | null }) {
  const url = usePhotoUrl(documentId ?? '', { enabled: documentId !== null })

  if (documentId === null) {
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-dashed border-default bg-subtle">
        <TriangleAlert className="h-4 w-4 text-muted-text" />
      </div>
    )
  }
  if (url.isError) {
    return (
      <div
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-danger bg-subtle"
        title="Could not load this photo"
      >
        <TriangleAlert className="h-4 w-4 text-danger" />
      </div>
    )
  }
  if (url.data === undefined) {
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-default bg-subtle">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-text" />
      </div>
    )
  }
  return (
    <img
      src={url.data}
      alt=""
      className="h-12 w-12 shrink-0 rounded-md border border-default object-cover"
    />
  )
}

/**
 * Choosing a problem type's stock photo, in either directory's Settings form.
 *
 * The photo is uploaded as an unattached library document and then pointed
 * at by id, rather than being owned by the problem type. It has to exist
 * before the type does — somebody adding "Mistletoe" picks the picture in the
 * same breath as the name — and a document that belongs to nothing is exactly
 * what the library scope already is.
 */
export function StockPhotoField({
  documentId,
  onChange,
  onBusyChange,
  hint,
}: {
  documentId: string | null
  onChange: (documentId: string | null) => void
  /** True while a photo uploads, so the form can hold Save until it has the id. */
  onBusyChange: (busy: boolean) => void
  hint: string
}) {
  const upload = useUploadDocument()
  const [uploading, setUploading] = React.useState(false)
  const [uploadError, setUploadError] = React.useState<string | null>(null)
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  // Set when Remove is clicked while this upload is still in flight. The
  // finished upload must not put the file back after that.
  const droppedDuringUpload = React.useRef(false)

  async function handleFile(file: File | undefined): Promise<void> {
    if (file === undefined) return
    setUploadError(null)
    setUploading(true)
    droppedDuringUpload.current = false
    onBusyChange(true)
    try {
      assertUsablePhoto(file)
      const document = await upload.mutateAsync({ file, meta: { kind: 'PHOTO' } })
      if (!droppedDuringUpload.current) onChange(document.id)
    } catch (caught) {
      setUploadError(errorMessage(caught, 'That could not be saved.'))
    } finally {
      setUploading(false)
      onBusyChange(false)
      if (inputRef.current !== null) inputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-1.5">
      <Label>Stock photo</Label>
      <div className="flex items-center gap-3">
        <StockPhotoThumb documentId={documentId} />
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_PHOTO_CONTENT_TYPES.join(',')}
          className="hidden"
          onChange={(event) => {
            void handleFile(event.target.files?.[0])
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={uploading}
          onClick={() => {
            inputRef.current?.click()
          }}
        >
          {uploading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Camera className="h-3.5 w-3.5" />
          )}
          {documentId === null ? 'Add photo' : 'Replace'}
        </Button>
        {documentId === null ? null : (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={uploading}
            onClick={() => {
              droppedDuringUpload.current = true
              onChange(null)
            }}
          >
            Remove
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-text">{hint}</p>
      {uploadError === null ? null : (
        <p className="text-xs text-danger">{uploadError}</p>
      )}
    </div>
  )
}
